import { ExternalLink, KeyRound, LogOut, X } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button } from '@/components/ui/button';
import { isNavItemActive } from '@/lib/admin-nav';
import { CLIENT_NAV, PUBLIC_SITE_URL } from '@/lib/client-nav';
import { signOut, useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';

/**
 * The client portal's sidebar — ROADMAP.md §5.1.
 *
 * Two things it does that the admin sidebar does not, both taken from the
 * prototype because the prototype is right about them:
 *
 *   - **The user card and sign-out live at the bottom**, not behind an avatar
 *     menu in the topbar. A homeowner opens this a few times a year; "where do I
 *     sign out" should not be a thing to discover.
 *   - **A link back to the public site.** Somebody who arrived from
 *     honeycombcleaning.com to check a booking wants a way back, and a portal
 *     with no exit is a portal people close the tab on.
 *
 * `isNavItemActive` comes from admin-nav rather than being written again — P4 is
 * one bug and it does not need two homes.
 */
export function ClientSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { data: session } = useSession();

  const user = session?.user;
  const initial = user?.name.trim().charAt(0).toUpperCase() ?? '?';

  async function onSignOut(): Promise<void> {
    await signOut();
    await navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-full flex-col">
      <nav aria-label="Portal sections" className="flex-1 overflow-y-auto px-3 py-4">
        <div className="mb-4 flex items-center justify-between px-2">
          <span className="text-h3 text-bark-700">Honeycomb</span>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Close menu"
            className="lg:hidden"
            onClick={onNavigate}
          >
            <X aria-hidden="true" />
          </Button>
        </div>

        <ul className="space-y-0.5">
          {CLIENT_NAV.map((item) => {
            const active = isNavItemActive(item.to, pathname);
            const Icon = item.icon;

            return (
              <li key={item.to}>
                <Link
                  to={item.to}
                  onClick={onNavigate}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    'flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label',
                    active
                      ? // ink-900 on honey-400 is 14.75:1. Never honey as text.
                        'bg-honey-400 text-ink-900'
                      : 'text-ink-700 hover:bg-honey-50',
                  )}
                >
                  <Icon aria-hidden="true" className="size-[18px] shrink-0" />
                  {item.label}
                </Link>
              </li>
            );
          })}
        </ul>

        <a
          href={PUBLIC_SITE_URL}
          // A new tab, because leaving the portal mid-task to look at the
          // marketing site is not what anybody meant by clicking this.
          target="_blank"
          rel="noreferrer"
          className="mt-4 flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label text-ink-700 hover:bg-honey-50"
        >
          <ExternalLink aria-hidden="true" className="size-[18px] shrink-0" />
          Back to the site
        </a>
      </nav>

      <div className="border-t border-ink-300 p-3">
        <div className="flex items-center gap-2.5 px-1 py-1.5">
          {/* ink-900 on honey-400 at 14.75:1. The prototype's avatar initial is
              honey on honey — 1.0:1, so the letter is simply not there (P27). */}
          <span
            aria-hidden="true"
            className="grid size-9 shrink-0 place-items-center rounded-full bg-honey-400 text-label font-semibold text-ink-900"
          >
            {initial}
          </span>
          <span className="min-w-0">
            <span className="block truncate text-label leading-tight text-ink-900">
              {user?.name}
            </span>
            {/* Honest about an absent value rather than an em-dash — this
                portal's own pattern, which design-system.md §7 says wins. */}
            <span className="block truncate text-meta leading-tight text-ink-500">
              {user?.email ?? <span className="italic">Signs in with a username</span>}
            </span>
          </span>
        </div>

        <Link
          to="/change-password"
          onClick={onNavigate}
          className="mt-1 flex min-h-touch items-center gap-2.5 rounded-md px-2 text-label text-ink-700 hover:bg-honey-50"
        >
          <KeyRound aria-hidden="true" className="size-[18px] shrink-0" />
          Change password
        </Link>

        <button
          type="button"
          onClick={() => void onSignOut()}
          className="flex min-h-touch w-full items-center gap-2.5 rounded-md px-2 text-label text-ink-700 hover:bg-honey-50"
        >
          <LogOut aria-hidden="true" className="size-[18px] shrink-0" />
          Sign out
        </button>
      </div>
    </div>
  );
}
